import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  CAPS_KINDS, KINDS, KIND_LABELS, blockId, blocksOf, elementsOf, elementsToFountain, fountainOf, kindAfter, kindBeside, kindTyped, layoutOf, measuresOf, paginate, parseFountain, wrapLines,
  type Block, type ElementKind, type Page, type Paper, type Part, type TitlePage, type WrappedLine,
} from '../../utils/screenplay';
import { Sheet, TitlePageView, pageMetrics } from './ScreenplayPages';
import { useCloseOnOutside } from './parts';

interface ScreenplayEditorProps {
  /** The screenplay, in Fountain; a change from outside (the model wrote, a version was restored) replaces what is being edited */
  text: string;
  paper: Paper;
  /** Pixels a point: the page's width over its width in points */
  scale: number;
  /** Pixels between two sheets, and above the first */
  pageGap: number;
  padTop: number;
  /** The page in view (1-based), from the card's scrolling: the sheets near it are drawn, the rest left out */
  visiblePage: number;
  onChange: (fountain: string) => void;
  /** How many pages there are now, and whether a title page comes first */
  onLaidOut?: (laid: { pages: number; hasTitlePage: boolean }) => void;
  /** The caret went to a page that is not in view: the card scrolls there */
  onRequestPage?: (page: number) => void;
  /** Counts up to put the caret at the end of the last line */
  focusAtEnd?: number;
  /** Words chosen across lines, as Fountain, or none: the card may offer them to the model */
  onSelectionChange?: (fountain: string | null) => void;
  /** Counts up each time the card asks the editor to let go of the chosen lines */
  letGo?: number;
}

interface Caret { id: string; at: number }
interface Snapshot { blocks: Block[]; caret: Caret | null }
/** A place in the screenplay: a block, and a place in its text */
interface Place { bi: number; at: number }
/** Words chosen across lines: from where the choosing began to where it is now */
interface Range { anchor: Place; focus: Place }

const HISTORY = 200;
const TYPING_RUN_MS = 1500;

/** Runs of spaces made one, the caret kept on its character */
const collapseSpaces = (text: string, caret: number): { text: string; caret: number } => {
  let out = '';
  let c = caret;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === ' ' && out.endsWith(' ')) { if (i < caret) c--; continue; }
    out += ch;
  }
  return { text: out, caret: c };
};

/** The text of the store, cleaned as the store cleans it, so a save coming back is known for our own */
const cleaned = (text: string) => text.replace(/\r\n/g, '\n').replace(/[ \t]+\n/g, '\n').trim();

const oneBlock = (blocks: Block[]): Block[] => (blocks.length ? blocks : [{ id: blockId(), kind: 'action', text: '' }]);

/**
 * The caret put in an area without the browser scrolling anything, then the area brought into
 * the card's own scroller by hand: the browser would scroll the board's frame too, which the
 * board pans by a transform and must stay unscrolled.
 */
const focusArea = (area: HTMLTextAreaElement, at: number) => {
  area.focus({ preventScroll: true });
  area.setSelectionRange(at, at);
  const scroller = area.closest<HTMLElement>('[data-pages-scroller]');
  const sheet = area.closest<HTMLElement>('[data-screenplay-page]');
  if (!scroller || !sheet || typeof scroller.getBoundingClientRect !== 'function') return;
  const a = area.getBoundingClientRect();
  const s = scroller.getBoundingClientRect();
  if (s.height <= 0 || scroller.clientHeight <= 0) return;
  if (a.top >= s.top && a.bottom <= s.bottom) return;
  // Out of view: its whole page is brought in, from the page's top (the scroller is a page tall).
  // Rectangles are in screen pixels, the scroller's own in its pixels before the board's zoom.
  const zoom = s.height / scroller.clientHeight;
  scroller.scrollTop += (sheet.getBoundingClientRect().top - s.top) / zoom - 8;
};

/** Where each wrapped line starts in the value shown (the lines joined by newlines) */
const valueStarts = (lines: WrappedLine[]): number[] => {
  const out: number[] = [];
  let at = 0;
  for (const line of lines) { out.push(at); at += line.text.length + 1; }
  return out;
};
/** The wrapped line a place in the text falls on: the last line that starts at or before it; a word broken at the width puts the place on the line after */
const lineOfModel = (lines: WrappedLine[], m: number): number => { let i = 0; while (i + 1 < lines.length && lines[i + 1].start <= m) i++; return i; };
const lineOfValue = (starts: number[], v: number): number => { let i = 0; while (i + 1 < starts.length && starts[i + 1] <= v) i++; return i; };
/** A place in the text as a place in the value shown */
const toValue = (lines: WrappedLine[], starts: number[], m: number): number => {
  const i = lineOfModel(lines, m);
  return starts[i] + (Math.min(m, lines[i].end) - lines[i].start);
};
/** A place in the value shown as a place in the text */
const toModel = (lines: WrappedLine[], starts: number[], v: number): number => {
  const i = lineOfValue(starts, v);
  return lines[i].start + Math.min(v - starts[i], lines[i].text.length);
};

const isBefore = (a: Place, b: Place) => a.bi < b.bi || (a.bi === b.bi && a.at <= b.at);
/** A range's two ends, the earlier first */
const endsOf = (range: Range): [Place, Place] => (isBefore(range.anchor, range.focus) ? [range.anchor, range.focus] : [range.focus, range.anchor]);

/**
 * The screenplay on its pages, every line written in place. Each element is a block; a block is
 * drawn as a text area on the page where the paginator put it, at the measures of its kind, its
 * words wrapped by the same rule as the printed page (a speech that breaks across pages is two
 * areas). Enter makes the next line what usually follows, Tab changes what a line is, Backspace at
 * the start joins it to the line before; Ctrl+Z undoes. Words are chosen across lines by dragging,
 * Shift and the arrows, Shift and a click, or Ctrl+A, then cut, copied, deleted or written over.
 * What is typed is written out as Fountain.
 */
export const ScreenplayEditor: React.FC<ScreenplayEditorProps> = ({ text, paper, scale, pageGap, padTop, visiblePage, onChange, onLaidOut, onRequestPage, focusAtEnd = 0, onSelectionChange, letGo = 0 }) => {
  const titlePage = useRef<TitlePage>({});
  const lastEmitted = useRef(text);
  const [blocks, setBlocks] = useState<Block[]>(() => {
    const parsed = parseFountain(text);
    titlePage.current = parsed.titlePage;
    return oneBlock(blocksOf(parsed));
  });
  const blocksRef = useRef(blocks);
  blocksRef.current = blocks;
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [multi, setMultiState] = useState<Range | null>(null);
  const multiRef = useRef<Range | null>(null);
  const setMulti = (range: Range | null) => { multiRef.current = range; setMultiState(range); };
  const [, bump] = useState(0);
  const areas = useRef(new Map<string, HTMLTextAreaElement>());
  const shown = useRef(new Map<string, string>());
  const pendingCaret = useRef<Caret | null>(null);
  const composing = useRef(false);
  const undo = useRef<Snapshot[]>([]);
  const redo = useRef<Snapshot[]>([]);
  const typingRun = useRef<{ id: string; time: number } | null>(null);
  const drag = useRef<{ from: Place; pointerId: number } | null>(null);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const menuRef = useCloseOnOutside<HTMLDivElement>(menuOpen, closeMenu);

  const m = pageMetrics(paper, scale);
  const hasTitlePage = !!titlePage.current.title;
  const sheetOf = (pageIndex: number) => pageIndex + (hasTitlePage ? 1 : 0);
  const sheetTop = (sheet: number) => padTop + sheet * (m.height + pageGap);

  // The pages, and where every block's lines lie on them
  const laid = useMemo(() => {
    const pages: Page[] = paginate({ titlePage: titlePage.current, elements: elementsOf(blocks), bodyStart: 0 }, paper);
    const parts = layoutOf(pages);
    const wrapped = blocks.map(block => (block.kind === 'page_break' ? [] : wrapLines(block.text, measuresOf(block.kind, paper).width)));
    const starts = wrapped.map(valueStarts);
    const byPage = new Map<number, { bi: number; pi: number; part: Part }[]>();
    const order: { bi: number; pi: number }[] = [];
    blocks.forEach((_, bi) => (parts.get(bi) ?? []).forEach((part, pi) => {
      order.push({ bi, pi });
      const list = byPage.get(part.page) ?? [];
      list.push({ bi, pi, part });
      byPage.set(part.page, list);
    }));
    return { pages, parts, wrapped, starts, byPage, order };
  }, [blocks, paper]);
  const { pages, parts, wrapped, starts, byPage, order } = laid;

  useEffect(() => { onLaidOut?.({ pages: pages.length, hasTitlePage }); }, [pages.length, hasTitlePage, onLaidOut]);

  const partKey = (bi: number, pi: number) => `${blocks[bi].id}:${pi}`;
  const partValue = (bi: number, part: Part) => wrapped[bi].slice(part.from, part.from + part.count).map(line => line.text).join('\n');

  const emit = (next: Block[]) => {
    const fountain = fountainOf(titlePage.current, next);
    lastEmitted.current = fountain;
    onChange(fountain);
  };
  /** Where the caret is now, in the text of its block */
  const caretNow = (): Caret | null => {
    const active = document.activeElement as HTMLTextAreaElement | null;
    const key = active?.dataset.scriptPart;
    if (!active || !key) return null;
    const [id, pi] = key.split(':');
    const bi = blocksRef.current.findIndex(block => block.id === id);
    if (bi < 0) return null;
    const part = parts.get(bi)?.[Number(pi)];
    if (!part) return null;
    return { id, at: toModel(wrapped[bi], starts[bi], (active.selectionStart ?? 0) + starts[bi][part.from]) };
  };
  /** The focused area's own selection, as places: the end it began at, and the end it is at */
  const nativeRange = (): Range | null => {
    const active = document.activeElement as HTMLTextAreaElement | null;
    const key = active?.dataset.scriptPart;
    if (!active || !key) return null;
    const [id, pi] = key.split(':');
    const bi = blocksRef.current.findIndex(block => block.id === id);
    const part = bi >= 0 ? parts.get(bi)?.[Number(pi)] : undefined;
    if (!part) return null;
    const off = starts[bi][part.from];
    const start = toModel(wrapped[bi], starts[bi], (active.selectionStart ?? 0) + off);
    const end = toModel(wrapped[bi], starts[bi], (active.selectionEnd ?? 0) + off);
    return active.selectionDirection === 'backward' ? { anchor: { bi, at: end }, focus: { bi, at: start } } : { anchor: { bi, at: start }, focus: { bi, at: end } };
  };

  // A change from outside (not the text we wrote out ourselves, as the store keeps it) replaces
  // the blocks; the caret stays on the same line, as far as the new lines allow
  useEffect(() => {
    if (cleaned(text) === cleaned(lastEmitted.current)) return;
    const parsed = parseFountain(text);
    titlePage.current = parsed.titlePage;
    lastEmitted.current = text;
    undo.current = [];
    redo.current = [];
    typingRun.current = null;
    setMulti(null);
    const next = oneBlock(blocksOf(parsed));
    const caret = caretNow();
    const bi = caret ? blocksRef.current.findIndex(block => block.id === caret.id) : -1;
    if (caret && bi >= 0 && next[bi]) pendingCaret.current = { id: next[bi].id, at: Math.min(caret.at, next[bi].text.length) };
    blocksRef.current = next;
    setBlocks(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  /** A change: remembered for undo (a run of typing in one line as one), shown, written out */
  const commit = (next: Block[], caret: Caret | null, mode: 'typing' | 'edit') => {
    const now = Date.now();
    const run = typingRun.current;
    const coalesce = mode === 'typing' && !!run && !!caret && run.id === caret.id && now - run.time < TYPING_RUN_MS;
    if (!coalesce) {
      undo.current.push({ blocks: blocksRef.current, caret: caretNow() });
      if (undo.current.length > HISTORY) undo.current.shift();
    }
    redo.current = [];
    typingRun.current = mode === 'typing' && caret ? { id: caret.id, time: now } : null;
    pendingCaret.current = caret;
    setMulti(null);
    blocksRef.current = next;
    setBlocks(next);
    emit(next);
  };
  const travel = (from: React.MutableRefObject<Snapshot[]>, to: React.MutableRefObject<Snapshot[]>) => {
    const snapshot = from.current.pop();
    if (!snapshot) return;
    to.current.push({ blocks: blocksRef.current, caret: caretNow() });
    typingRun.current = null;
    pendingCaret.current = snapshot.caret ?? { id: snapshot.blocks[0].id, at: 0 };
    setMulti(null);
    blocksRef.current = snapshot.blocks;
    setBlocks(snapshot.blocks);
    emit(snapshot.blocks);
  };

  // What is typed: the area's value against what it showed, the difference put into the block's text
  const onInput = (bi: number, pi: number, area: HTMLTextAreaElement) => {
    if (composing.current) return;
    const block = blocks[bi];
    const part = parts.get(bi)?.[pi];
    if (!part) return;
    const key = partKey(bi, pi);
    const prev = shown.current.get(key) ?? '';
    const next = area.value;
    if (prev === next) return;
    const selStart = area.selectionStart ?? next.length;
    const capS = Math.max(0, next.length - selStart);
    let s = 0;
    while (s < capS && s < prev.length && prev[prev.length - 1 - s] === next[next.length - 1 - s]) s++;
    let p = 0;
    const maxP = Math.min(prev.length - s, next.length - s);
    while (p < maxP && prev[p] === next[p]) p++;
    const lines = wrapped[bi];
    const vOff = starts[bi][part.from];
    const from = toModel(lines, starts[bi], p + vOff);
    const to = toModel(lines, starts[bi], prev.length - s + vOff);
    let inserted = next.slice(p, next.length - s).replace(/\t/g, ' ');
    if (block.kind !== 'action') inserted = inserted.replace(/\n+/g, ' ');
    const raw = block.text.slice(0, from) + inserted + block.text.slice(to);
    const { text: value, caret } = collapseSpaces(raw, from + inserted.length);
    const kind = kindTyped(block.kind, value);
    commit(blocks.map((b, i) => (i === bi ? { ...b, text: value, kind } : b)), { id: block.id, at: caret }, 'typing');
  };

  /** The selection of an area, as places in its block's text */
  const selectionOf = (bi: number, pi: number, area: HTMLTextAreaElement): { start: number; end: number } => {
    const part = parts.get(bi)![pi];
    const vOff = starts[bi][part.from];
    return { start: toModel(wrapped[bi], starts[bi], (area.selectionStart ?? 0) + vOff), end: toModel(wrapped[bi], starts[bi], (area.selectionEnd ?? 0) + vOff) };
  };

  /** The blocks with one of them split at a place: the words after the place become the next line */
  const split = (list: Block[], bi: number, start: number, end: number): { next: Block[]; caret: Caret } => {
    const block = list[bi];
    const before = block.text.slice(0, start).trimEnd();
    const after = block.text.slice(end).replace(/^ +/, '');
    if (!block.text.trim() && block.kind !== 'action') {
      // Enter on an empty line that is not action makes it action, rather than one more empty line
      return { next: list.map((b, i) => (i === bi ? { ...b, kind: 'action' } : b)), caret: { id: block.id, at: 0 } };
    }
    const made: Block = { id: blockId(), kind: after ? block.kind : kindAfter(block.kind), text: after };
    return { next: [...list.slice(0, bi), { ...block, text: before }, made, ...list.slice(bi + 1)], caret: { id: made.id, at: 0 } };
  };
  const splitAt = (bi: number, start: number, end: number) => {
    const { next, caret } = split(blocks, bi, start, end);
    commit(next, caret, 'edit');
  };

  /** The blocks with the words between two places taken out, these put in their place */
  const spliced = (list: Block[], start: Place, end: Place, words: string): { next: Block[]; caret: Caret } => {
    const first = list[start.bi];
    const last = list[end.bi];
    const merged: Block = { ...first, text: `${first.text.slice(0, start.at)}${words}${last.text.slice(end.at)}` };
    return { next: [...list.slice(0, start.bi), merged, ...list.slice(end.bi + 1)], caret: { id: first.id, at: start.at + words.length } };
  };
  const replaceRange = (start: Place, end: Place, words: string) => {
    const { next, caret } = spliced(blocks, start, end, words);
    commit(next, caret, 'edit');
  };
  /** The chosen words as Fountain, for the clipboard */
  const rangeFountain = (start: Place, end: Place): string =>
    elementsToFountain(blocks.slice(start.bi, end.bi + 1).map((block, i, all) => ({ kind: block.kind, text: block.text.slice(i === 0 ? start.at : 0, i === all.length - 1 ? end.at : undefined) })));

  const joinWithPrevious = (bi: number) => {
    if (bi === 0) return;
    const block = blocks[bi];
    const prev = blocks[bi - 1];
    if (prev.kind === 'page_break') { commit([...blocks.slice(0, bi - 1), ...blocks.slice(bi)], { id: block.id, at: 0 }, 'edit'); return; }
    if (!block.text) { commit([...blocks.slice(0, bi), ...blocks.slice(bi + 1)], { id: prev.id, at: prev.text.length }, 'edit'); return; }
    if (!prev.text) { commit([...blocks.slice(0, bi - 1), ...blocks.slice(bi)], { id: block.id, at: 0 }, 'edit'); return; }
    const glue = /\s$/.test(prev.text) || /^\s/.test(block.text) ? '' : ' ';
    commit([...blocks.slice(0, bi - 1), { ...prev, text: `${prev.text}${glue}${block.text}` }, ...blocks.slice(bi + 1)], { id: prev.id, at: prev.text.length }, 'edit');
  };

  const joinWithNext = (bi: number) => {
    if (bi >= blocks.length - 1) return;
    const block = blocks[bi];
    const next = blocks[bi + 1];
    if (next.kind === 'page_break') { commit([...blocks.slice(0, bi + 1), ...blocks.slice(bi + 2)], { id: block.id, at: block.text.length }, 'edit'); return; }
    if (!next.text) { commit([...blocks.slice(0, bi + 1), ...blocks.slice(bi + 2)], { id: block.id, at: block.text.length }, 'edit'); return; }
    if (!block.text) { commit([...blocks.slice(0, bi), ...blocks.slice(bi + 1)], { id: next.id, at: 0 }, 'edit'); return; }
    const glue = /\s$/.test(block.text) || /^\s/.test(next.text) ? '' : ' ';
    commit([...blocks.slice(0, bi), { ...block, text: `${block.text}${glue}${next.text}` }, ...blocks.slice(bi + 2)], { id: block.id, at: block.text.length }, 'edit');
  };

  const setKind = (bi: number, kind: ElementKind) => {
    const block = blocks[bi];
    if (block.kind === kind) return;
    const caret = caretNow();
    commit(blocks.map((b, i) => (i === bi ? { ...b, kind } : b)), caret && caret.id === block.id ? caret : { id: block.id, at: block.text.length }, 'edit');
  };

  /** The caret put in another area: the one before or after, at a column */
  const goTo = (index: number, where: 'start' | 'end' | { fromTop: number } | { fromBottom: number }) => {
    const at = order[index];
    if (!at) return false;
    const area = areas.current.get(partKey(at.bi, at.pi));
    if (!area) { onRequestPage?.(parts.get(at.bi)![at.pi].page + 1); return true; }
    const value = area.value;
    let pos: number;
    if (where === 'start') pos = 0;
    else if (where === 'end') pos = value.length;
    else if ('fromTop' in where) { const first = value.indexOf('\n'); pos = Math.min(where.fromTop, first < 0 ? value.length : first); }
    else { const lastStart = value.lastIndexOf('\n') + 1; pos = Math.min(lastStart + where.fromBottom, value.length); }
    focusArea(area, pos);
    setFocusedId(blocks[at.bi].id);
    return true;
  };

  // ── Words chosen across lines ──
  /** The place a point on the screen falls on, within an area: the line under it, the column nearest */
  const placeIn = (area: HTMLTextAreaElement, x: number, y: number): Place | null => {
    const [id, piText] = (area.dataset.scriptPart ?? '').split(':');
    const bi = blocks.findIndex(block => block.id === id);
    const part = bi >= 0 ? parts.get(bi)?.[Number(piText)] : undefined;
    if (!part) return null;
    const rect = area.getBoundingClientRect();
    const zoom = rect.height > 0 && part.count > 0 ? rect.height / (part.count * m.lineHeight) : 1;
    const row = Math.max(0, Math.min(part.count - 1, Math.floor((y - rect.top) / (m.lineHeight * zoom))));
    const line = wrapped[bi][part.from + row];
    const measure = measuresOf(blocks[bi].kind, paper);
    const slack = measure.align === 'right' ? measure.width - line.text.length : measure.align === 'center' ? Math.floor((measure.width - line.text.length) / 2) : 0;
    const col = Math.max(0, Math.min(line.text.length, Math.round((x - rect.left) / (m.charWidth * zoom) - slack)));
    return { bi, at: line.start + col };
  };
  const placeAt = (x: number, y: number): Place | null => {
    const el = typeof document.elementFromPoint === 'function' ? document.elementFromPoint(x, y) : null;
    const area = (el as HTMLElement | null)?.closest?.('[data-script-part]') as HTMLTextAreaElement | null;
    return area ? placeIn(area, x, y) : null;
  };
  /** A place moved one line up or down, or one character along, into the line before or after when at an edge */
  const stepped = (from: Place, key: string, column: number | null): Place => {
    const lines = wrapped[from.bi];
    const li = lineOfModel(lines, from.at);
    const col = column ?? from.at - lines[li].start;
    const lineStart = (bi: number, line: number) => ({ bi, at: wrapped[bi][line].start + Math.min(col, wrapped[bi][line].text.length) });
    const nextBlock = (bi: number, step: number) => { let i = bi + step; while (i >= 0 && i < blocks.length && !wrapped[i].length) i += step; return i >= 0 && i < blocks.length ? i : -1; };
    if (key === 'ArrowDown') {
      if (li < lines.length - 1) return lineStart(from.bi, li + 1);
      const bi = nextBlock(from.bi, 1);
      return bi < 0 ? { bi: from.bi, at: blocks[from.bi].text.length } : lineStart(bi, 0);
    }
    if (key === 'ArrowUp') {
      if (li > 0) return lineStart(from.bi, li - 1);
      const bi = nextBlock(from.bi, -1);
      return bi < 0 ? { bi: from.bi, at: 0 } : lineStart(bi, wrapped[bi].length - 1);
    }
    if (key === 'ArrowRight') {
      if (from.at < blocks[from.bi].text.length) return { bi: from.bi, at: from.at + 1 };
      const bi = nextBlock(from.bi, 1);
      return bi < 0 ? from : { bi, at: 0 };
    }
    if (from.at > 0) return { bi: from.bi, at: from.at - 1 };
    const bi = nextBlock(from.bi, -1);
    return bi < 0 ? from : { bi, at: blocks[bi].text.length };
  };
  /** The column kept while the choosing goes up and down */
  const column = useRef<number | null>(null);
  /** Choosing extended, or begun, with Shift and an arrow */
  const extend = (key: string) => {
    const range = multiRef.current ?? nativeRange();
    if (!range) return;
    if (key === 'ArrowUp' || key === 'ArrowDown') { if (column.current === null) column.current = range.focus.at - wrapped[range.focus.bi][lineOfModel(wrapped[range.focus.bi], range.focus.at)].start; }
    else column.current = null;
    const focus = stepped(range.focus, key, column.current);
    const next = { anchor: range.anchor, focus };
    if (focus.bi === range.anchor.bi && (parts.get(focus.bi)?.length ?? 1) === 1) {
      // Within one area: the area's own selection
      setMulti(null);
      const area = areas.current.get(partKey(focus.bi, 0));
      if (!area) return;
      const a = toValue(wrapped[focus.bi], starts[focus.bi], range.anchor.at);
      const f = toValue(wrapped[focus.bi], starts[focus.bi], focus.at);
      area.focus({ preventScroll: true });
      area.setSelectionRange(Math.min(a, f), Math.max(a, f), f < a ? 'backward' : 'forward');
      return;
    }
    setMulti(next);
  };
  const selectAll = () => {
    const first = blocks.findIndex(block => wrapped[blocks.indexOf(block)].length > 0);
    let last = blocks.length - 1;
    while (last > 0 && !wrapped[last].length) last--;
    if (first < 0) return;
    if (first === last && (parts.get(first)?.length ?? 1) === 1) {
      const area = areas.current.get(partKey(first, 0));
      area?.focus({ preventScroll: true });
      area?.setSelectionRange(0, area.value.length);
      return;
    }
    setMulti({ anchor: { bi: first, at: 0 }, focus: { bi: last, at: blocks[last].text.length } });
  };
  /** The caret put at one end of what was chosen, the choosing over */
  const collapseTo = (place: Place) => {
    setMulti(null);
    pendingCaret.current = { id: blocks[place.bi].id, at: place.at };
    bump(n => n + 1);
  };

  const onRootPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const area = (e.target as HTMLElement).closest?.('[data-script-part]') as HTMLTextAreaElement | null;
    if (!area || e.button !== 0) return;
    const here = placeIn(area, e.clientX, e.clientY);
    if (!here) return;
    if (e.shiftKey) {
      const from = multiRef.current?.anchor ?? nativeRange()?.anchor ?? null;
      if (from && (from.bi !== here.bi || (parts.get(here.bi)?.length ?? 1) > 1)) { e.preventDefault(); setMulti({ anchor: from, focus: here }); return; }
    }
    column.current = null;
    drag.current = { from: here, pointerId: e.pointerId };
    if (multiRef.current) setMulti(null);
  };
  const onRootPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    if (!(e.buttons & 1)) { drag.current = null; return; }
    const to = placeAt(e.clientX, e.clientY);
    if (!to) return;
    if (to.bi !== d.from.bi) setMulti({ anchor: d.from, focus: to });
    else if (multiRef.current) setMulti(null);
  };
  const onRootPointerUp = (e: React.PointerEvent<HTMLDivElement>) => { if (drag.current?.pointerId === e.pointerId) drag.current = null; };
  const onCopyOrCut = (e: React.ClipboardEvent, cut: boolean) => {
    const range = multiRef.current;
    if (!range) return;
    const [start, end] = endsOf(range);
    e.clipboardData.setData('text/plain', rangeFountain(start, end));
    e.preventDefault();
    if (cut) replaceRange(start, end, '');
  };

  const onKeyDown = (bi: number, pi: number, e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const area = e.currentTarget;
    if (composing.current || e.nativeEvent.isComposing) return;
    const block = blocks[bi];
    const list = parts.get(bi) ?? [];
    const index = order.findIndex(o => o.bi === bi && o.pi === pi);
    const value = area.value;
    const selStart = area.selectionStart ?? 0;
    const selEnd = area.selectionEnd ?? 0;
    const collapsed = selStart === selEnd;
    const ctrl = e.ctrlKey || e.metaKey;
    const letter = e.key.length === 1 ? e.key.toLowerCase() : e.key;
    const arrow = e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight';

    if (ctrl && !e.altKey && letter === 'a') { e.preventDefault(); selectAll(); return; }
    // Words chosen across lines: what a key does to them
    const range = multiRef.current;
    if (range) {
      const [start, end] = endsOf(range);
      if (e.key === 'Escape') { e.preventDefault(); collapseTo(range.focus); return; }
      if (ctrl && (letter === 'c' || letter === 'x')) return; // the copy and cut events take it
      if (ctrl && !e.altKey && (letter === 'z' || letter === 'y')) { /* undone below */ }
      else if (e.key === 'Backspace' || e.key === 'Delete') { e.preventDefault(); replaceRange(start, end, ''); return; }
      else if (e.key === 'Enter') {
        e.preventDefault();
        const taken = spliced(blocks, start, end, '');
        const { next, caret } = split(taken.next, start.bi, start.at, start.at);
        commit(next, caret, 'edit');
        return;
      }
      else if (arrow) { e.preventDefault(); if (e.shiftKey) extend(e.key); else collapseTo(e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? start : end); return; }
      else if (e.key.length === 1 && !ctrl && !e.altKey) { e.preventDefault(); replaceRange(start, end, e.key); return; }
      else if (e.key === 'Tab') { setMulti(null); }
      else if (!ctrl) { setMulti(null); }
    } else if (e.shiftKey && arrow && !ctrl) {
      // Choosing that reaches the edge of this area goes on into the line before or after
      const atTop = !value.slice(0, Math.min(selStart, selEnd)).includes('\n') && (e.key === 'ArrowUp' || (e.key === 'ArrowLeft' && (area.selectionDirection === 'backward' ? selStart : selEnd) === 0));
      const atBottom = !value.slice(Math.max(selStart, selEnd)).includes('\n') && (e.key === 'ArrowDown' || (e.key === 'ArrowRight' && (area.selectionDirection === 'backward' ? selStart : selEnd) === value.length));
      const leaving = (e.key === 'ArrowUp' || e.key === 'ArrowLeft') ? atTop : atBottom;
      if (leaving && (e.key === 'ArrowUp' || e.key === 'ArrowDown' ? true : (e.key === 'ArrowLeft' ? (area.selectionDirection === 'backward' ? selStart : selEnd) === 0 : (area.selectionDirection === 'backward' ? selStart : selEnd) === value.length))) { e.preventDefault(); extend(e.key); return; }
      column.current = null;
    } else if (!e.shiftKey) column.current = null;

    if (ctrl && !e.altKey && (letter === 'z' || letter === 'y')) {
      e.preventDefault();
      if (letter === 'y' || e.shiftKey) travel(redo, undo); else travel(undo, redo);
      return;
    }
    if (ctrl && /^[1-7]$/.test(e.key)) { e.preventDefault(); setKind(bi, KINDS[Number(e.key) - 1]); return; }
    if (e.key === 'Tab') { e.preventDefault(); setKind(bi, kindBeside(block.kind, e.shiftKey)); return; }
    if (e.key === 'Escape') { e.preventDefault(); if (menuOpen) setMenuOpen(false); else { if (multiRef.current) setMulti(null); area.blur(); } return; }
    if (e.key === 'Enter') {
      e.preventDefault();
      const { start, end } = selectionOf(bi, pi, area);
      if (e.shiftKey && block.kind === 'action') {
        const raw = `${block.text.slice(0, start)}\n${block.text.slice(end)}`;
        commit(blocks.map((b, i) => (i === bi ? { ...b, text: raw } : b)), { id: block.id, at: start + 1 }, 'edit');
      } else splitAt(bi, start, end);
      return;
    }
    if (e.key === 'Backspace' && collapsed && selStart === 0 && pi === 0) { e.preventDefault(); joinWithPrevious(bi); return; }
    if (e.key === 'Delete' && collapsed && selStart === value.length && pi === list.length - 1) { e.preventDefault(); joinWithNext(bi); return; }
    if (e.key === 'ArrowUp' && !e.shiftKey && !value.slice(0, selStart).includes('\n')) { if (goTo(index - 1, { fromBottom: selStart })) e.preventDefault(); return; }
    if (e.key === 'ArrowDown' && !e.shiftKey && !value.slice(selEnd).includes('\n')) {
      const col = selStart - (value.lastIndexOf('\n', selStart - 1) + 1);
      if (goTo(index + 1, { fromTop: col })) e.preventDefault();
      return;
    }
    if (e.key === 'ArrowLeft' && !e.shiftKey && collapsed && selStart === 0) { if (goTo(index - 1, 'end')) e.preventDefault(); return; }
    if (e.key === 'ArrowRight' && !e.shiftKey && collapsed && selStart === value.length) { if (goTo(index + 1, 'start')) e.preventDefault(); return; }
  };

  /** Lines pasted in: read as Fountain and put in as lines of their kinds, the line split around them */
  const onPaste = (bi: number, pi: number, e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const data = e.clipboardData.getData('text/plain').replace(/\r\n?/g, '\n');
    const range = multiRef.current;
    if (!data.includes('\n') && !range) return;
    e.preventDefault();
    // Words chosen across lines go first, whatever is pasted takes their place
    let list = blocks;
    let at = bi;
    let { start, end } = selectionOf(bi, pi, e.currentTarget);
    if (range) {
      const [from, to] = endsOf(range);
      list = spliced(blocks, from, to, '').next;
      at = from.bi;
      start = end = from.at;
    }
    const block = list[at];
    const parsed = parseFountain(data);
    if (parsed.elements.length <= 1 && !data.includes('\n\n')) {
      const inserted = block.kind === 'action' ? data : data.replace(/\n+/g, ' ');
      const { text: value, caret } = collapseSpaces(`${block.text.slice(0, start)}${inserted}${block.text.slice(end)}`, start + inserted.length);
      commit(list.map((b, i) => (i === at ? { ...b, text: value, kind: kindTyped(b.kind, value) } : b)), { id: block.id, at: caret }, 'edit');
      return;
    }
    const before = block.text.slice(0, start).trimEnd();
    const after = block.text.slice(end).replace(/^ +/, '');
    const made = blocksOf(parsed);
    const tail = after ? [{ id: blockId(), kind: block.kind, text: after }] : [];
    const next = [...list.slice(0, at), ...(before ? [{ ...block, text: before }] : []), ...made, ...tail, ...list.slice(at + 1)];
    const last = made[made.length - 1];
    commit(next, { id: last.id, at: last.text.length }, 'edit');
  };

  /** A press on the paper itself puts the caret on the nearest line above it */
  const onSheetDown = (pageIndex: number) => (e: React.PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('textarea, button, [role="menu"]')) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const line = (e.clientY - rect.top - m.top) / m.lineHeight;
    const here = byPage.get(pageIndex) ?? [];
    const above = [...here].reverse().find(entry => entry.part.top <= line) ?? here[0];
    const target = above ? order.findIndex(o => o.bi === above.bi && o.pi === above.pi) : order.length - 1;
    setMulti(null);
    goTo(target, 'end');
  };

  // The areas show their lines; the caret goes where the last change put it
  useLayoutEffect(() => {
    for (const [key, area] of areas.current) {
      const [id, pi] = key.split(':');
      const bi = blocks.findIndex(block => block.id === id);
      const part = bi >= 0 ? parts.get(bi)?.[Number(pi)] : undefined;
      if (!part) continue;
      const value = partValue(bi, part);
      shown.current.set(key, value);
      if (area.value !== value && !composing.current) area.value = value;
    }
    const want = pendingCaret.current;
    if (!want) return;
    const bi = blocks.findIndex(block => block.id === want.id);
    const list = bi >= 0 ? parts.get(bi) ?? [] : [];
    if (bi < 0 || list.length === 0) { pendingCaret.current = null; return; }
    const v = toValue(wrapped[bi], starts[bi], Math.min(want.at, blocks[bi].text.length));
    const line = lineOfValue(starts[bi], v);
    let pi = list.findIndex(part => line >= part.from && line < part.from + part.count);
    if (pi < 0) pi = list.length - 1;
    const area = areas.current.get(partKey(bi, pi));
    if (!area) { onRequestPage?.(list[pi].page + 1); return; }
    pendingCaret.current = null;
    const local = Math.max(0, Math.min(area.value.length, v - starts[bi][list[pi].from]));
    focusArea(area, local);
    setFocusedId(blocks[bi].id);
    if (list[pi].page !== visiblePage - 1) onRequestPage?.(list[pi].page + 1);
  });

  // What is chosen across lines, told to the card as Fountain
  useEffect(() => {
    if (!onSelectionChange) return;
    if (!multi) { onSelectionChange(null); return; }
    const [start, end] = endsOf(multi);
    onSelectionChange(rangeFountain(start, end));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multi, onSelectionChange]);

  // Asked from outside: the chosen lines let go
  const lastLetGo = useRef(letGo);
  useEffect(() => {
    if (letGo === lastLetGo.current) return;
    lastLetGo.current = letGo;
    setMulti(null);
  }, [letGo]);

  // Asked from outside: the caret at the very end
  const lastFocusAtEnd = useRef(0);
  useEffect(() => {
    if (focusAtEnd === lastFocusAtEnd.current) return;
    lastFocusAtEnd.current = focusAtEnd;
    const last = blocks[blocks.length - 1];
    pendingCaret.current = { id: last.id, at: last.text.length };
    setFocusedId(last.id);
  }, [focusAtEnd, blocks]);

  const areaRef = (key: string) => (el: HTMLTextAreaElement | null) => {
    if (el) areas.current.set(key, el);
    else { areas.current.delete(key); shown.current.delete(key); }
  };

  const focusedIndex = focusedId ? blocks.findIndex(block => block.id === focusedId) : -1;
  const sheets = pages.length + (hasTitlePage ? 1 : 0);
  const near = (sheet: number) => Math.abs(sheet - (visiblePage - 1 + (hasTitlePage ? 1 : 0))) <= 1;

  // The chosen words, drawn over their lines
  const chosen: { x: number; y: number; w: number; h: number }[] = [];
  if (multi) {
    const [start, end] = endsOf(multi);
    for (let bi = start.bi; bi <= end.bi; bi++) {
      const lines = wrapped[bi];
      if (!lines.length) continue;
      const a = bi === start.bi ? start.at : 0;
      const b = bi === end.bi ? end.at : blocks[bi].text.length;
      const la = lineOfModel(lines, a);
      const lb = lineOfModel(lines, b);
      const list = parts.get(bi) ?? [];
      const measure = measuresOf(blocks[bi].kind, paper);
      for (let i = la; i <= lb; i++) {
        const part = list.find(p => i >= p.from && i < p.from + p.count);
        if (!part || !near(sheetOf(part.page))) continue;
        const line = lines[i];
        const c0 = i === la ? a - line.start : 0;
        const c1 = i === lb ? b - line.start : line.text.length;
        const slack = measure.align === 'right' ? measure.width - line.text.length : measure.align === 'center' ? Math.floor((measure.width - line.text.length) / 2) : 0;
        chosen.push({ x: m.left + (measure.indent + slack + c0) * m.charWidth, y: sheetTop(sheetOf(part.page)) + m.top + (part.top + i - part.from) * m.lineHeight, w: Math.max(m.charWidth / 2, (c1 - c0) * m.charWidth), h: m.lineHeight });
      }
    }
  }

  return (
    <div
      className="relative"
      style={{ height: padTop + sheets * (m.height + pageGap), width: m.width }}
      onPointerDown={onRootPointerDown}
      onPointerMove={onRootPointerMove}
      onPointerUp={onRootPointerUp}
      onPointerCancel={onRootPointerUp}
      onCopy={(e) => onCopyOrCut(e, false)}
      onCut={(e) => onCopyOrCut(e, true)}
      data-screenplay-editor
    >
      {hasTitlePage && near(0) && (
        <div className="absolute left-0" style={{ top: sheetTop(0) }}><TitlePageView titlePage={titlePage.current} paper={paper} scale={scale} /></div>
      )}
      {pages.map((page, p) => near(sheetOf(p)) && (
        <div key={page.number} className="absolute left-0" style={{ top: sheetTop(sheetOf(p)) }}>
          <Sheet paper={paper} scale={scale} data={String(page.number)} onPointerDown={onSheetDown(p)}>
            {page.number > 1 && <span className="absolute select-none" style={{ right: m.right, top: 36 * scale }}>{page.number}.</span>}
            {page.lines.map((line, i) => line.element < 0 && line.kind !== 'blank' && (
              <div key={`l${i}`} className="absolute whitespace-pre select-none" style={{ left: m.left + line.indent * m.charWidth, top: m.top + i * m.lineHeight, height: m.lineHeight }}>{line.text}</div>
            ))}
            {(byPage.get(p) ?? []).map(({ bi, pi, part }) => {
              const block = blocks[bi];
              const measure = measuresOf(block.kind, paper);
              const key = partKey(bi, pi);
              return (
                <textarea
                  key={key}
                  ref={areaRef(key)}
                  defaultValue={partValue(bi, part)}
                  className="ns-script-block"
                  style={{
                    left: m.left + measure.indent * m.charWidth,
                    top: m.top + part.top * m.lineHeight,
                    width: measure.width * m.charWidth + 2,
                    height: part.count * m.lineHeight,
                    fontWeight: block.kind === 'scene_heading' ? 700 : 400,
                    textTransform: CAPS_KINDS.has(block.kind) ? 'uppercase' : undefined,
                    textAlign: measure.align,
                  }}
                  wrap="off"
                  spellCheck={!CAPS_KINDS.has(block.kind)}
                  aria-label={`${KIND_LABELS[block.kind]}, page ${page.number}`}
                  onInput={(e) => onInput(bi, pi, e.currentTarget)}
                  onKeyDown={(e) => onKeyDown(bi, pi, e)}
                  onPaste={(e) => onPaste(bi, pi, e)}
                  onFocus={() => setFocusedId(block.id)}
                  // The chosen lines stay chosen when the keyboard goes elsewhere (to the chat, to ask about them); a click on a line, a key in it, Escape or the card let go of them
                  onBlur={(e) => { if (!(e.relatedTarget as HTMLElement | null)?.closest('[data-script-menu]')) { setFocusedId(current => (current === block.id ? null : current)); setMenuOpen(false); } }}
                  onCompositionStart={() => { composing.current = true; }}
                  onCompositionEnd={(e) => { composing.current = false; onInput(bi, pi, e.currentTarget); }}
                  data-script-part={key}
                  data-script-block={block.id}
                  data-script-kind={block.kind}
                />
              );
            })}
            {/* What the line being written is, by it in the margin: a press changes it */}
            {focusedIndex >= 0 && (byPage.get(p) ?? []).filter(entry => entry.bi === focusedIndex && entry.pi === 0).map(({ part }) => (
              <div key="kind" className="absolute z-10" style={{ left: 8, top: m.top + part.top * m.lineHeight - 3 }} data-script-menu>
                <button
                  type="button"
                  className="ns-chip ns-chip-sm ns-chip-btn ns-chip-lavender font-sans"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  title="What this line is (Tab changes it)"
                  onPointerDown={(e) => e.preventDefault()}
                  onClick={() => setMenuOpen(open => !open)}
                  data-script-kind-chip
                >
                  {KIND_LABELS[blocks[focusedIndex].kind]} <ChevronDown size={12} aria-hidden="true" />
                </button>
                {menuOpen && (
                  <div ref={menuRef} className="ns-menu absolute left-0 top-full mt-1 font-sans" role="menu" onPointerDown={(e) => e.preventDefault()}>
                    {KINDS.map((kind, i) => (
                      <button key={kind} type="button" role="menuitemradio" aria-checked={blocks[focusedIndex].kind === kind} className="ns-menu-item" onClick={() => { setKind(focusedIndex, kind); setMenuOpen(false); }}>
                        {KIND_LABELS[kind]}<span className="ml-auto pl-4 text-2xs text-ink-faint">Ctrl+{i + 1}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </Sheet>
        </div>
      ))}
      {chosen.map((r, i) => <div key={i} className="absolute pointer-events-none bg-lavender/60" style={{ left: r.x, top: r.y, width: r.w, height: r.h }} data-script-selection />)}
    </div>
  );
};

export default ScreenplayEditor;
